import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  Select,
  Switch,
} from '@mui/material';
import { LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import NorthIndianChart from './NorthIndianChart';
import BhavaChalitChart from './BhavaChalitChart';
import { getHouseShort, getPlanetAbbr, getSignAbbr } from '../../i18n/astrology';
import { useCurrentTransitOverlay } from '../../hooks/useCurrentTransitOverlay';
import { isOuterPlanetKey, useUserPreferences } from '../../contexts/UserPreferencesContext';

interface BirthChartDisplayProps {
  chartData: {
    planets: {
      [key: string]: {
        name: string;
        longitude: number;
        sign: { name: string; number: number };
        house: number;
        bhavaChalitHouse?: number;
        nakshatra: { name: string; lord: string };
        degree: { degrees: number; minutes: number; seconds: number; formatted: string };
        retrograde?: boolean;
      }
    };
    houses: {
      cusps: {
        [key: number]: {
          longitude: number;
          sign: { name: string; number: number };
        }
      };
      ascendant: {
        longitude: number;
        sign: { name: string; number: number };
        degree: { degrees: number; minutes: number; seconds: number; formatted: string };
        nakshatra: { name: string; lord: string };
      };
    };
    birthDetails: {
      name: string;
      dateOfBirth: string;
      timeOfBirth: string;
      placeOfBirth: {
        name: string;
        latitude: number;
        longitude: number;
      };
      gender: string;
    };
    bhavaChalit?: {
      cusps: {
        [key: number]: {
          longitude: number;
          sign: { name: string; number: number };
          degree: { degrees: number; minutes: number; seconds: number; formatted: string };
          nakshatra: { name: string; lord: string };
        };
      };
      system: string;
    };
  };
  chartSize?: number;
  rotationHouse?: number;
  onRotationHouseChange?: (house: number) => void;
}

const displayChartName = (value?: string | null) => String(value || '').trim() || 'N/A';

const BirthChartDisplay: React.FC<BirthChartDisplayProps> = ({ chartData, chartSize = 400, rotationHouse, onRotationHouseChange }) => {
  const [internalRotationHouse, setInternalRotationHouse] = useState<number>(1);
  const [showTransitOverlay, setShowTransitOverlay] = useState(false);
  const { t } = useTranslation();
  const { chartStyle, showOuterPlanets } = useUserPreferences();
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const isSignFixedStyle = chartStyle === 'south' || chartStyle === 'east';
  const { transitChart, loading: transitLoading, error: transitError } = useCurrentTransitOverlay(chartData, showTransitOverlay);
  const selectedRotationHouse = rotationHouse ?? internalRotationHouse;
  const effectiveRotationHouse = isSignFixedStyle ? 1 : selectedRotationHouse;

  const handleRotationHouseChange = (house: number) => {
    if (onRotationHouseChange) {
      onRotationHouseChange(house);
      return;
    }
    setInternalRotationHouse(house);
  };

  const rotateHouseNumber = (houseNumber?: number) => {
    if (!houseNumber) return undefined;
    return ((houseNumber - effectiveRotationHouse + 12) % 12) + 1;
  };

  // Debug: Check what chartData contains
  React.useEffect(() => {
    console.log('BirthChartDisplay received chartData:', {
      hasBhavaChalit: !!chartData?.bhavaChalit,
      bhavaChalitKeys: chartData?.bhavaChalit ? Object.keys(chartData.bhavaChalit) : [],
      chartDataKeys: Object.keys(chartData || {}),
      sampleBhavaCusp: chartData?.bhavaChalit?.cusps?.[1]
    });
  }, [chartData]);

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            🔮 {t('astrology.birthChart')}
          </Typography>
          <Typography variant="h6" color="primary">
            {displayChartName(chartData.birthDetails?.name)}
          </Typography>
        </Box>

        {!isSignFixedStyle && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('astrology.rotateFromHouse')}</InputLabel>
            <Select
              value={selectedRotationHouse}
              label={t('astrology.rotateFromHouse')}
              onChange={(event) => handleRotationHouseChange(Number(event.target.value))}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
                <MenuItem key={house} value={house}>
                  {t('astrology.house', { number: house })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        )}

        {!isSignFixedStyle && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
            {t('astrology.rotatedFromHouse', { number: selectedRotationHouse })}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={showTransitOverlay} onChange={(event) => setShowTransitOverlay(event.target.checked)} />}
            label={t('astrology.showCurrentTransitOverlay')}
            sx={{ m: 0 }}
          />
          {transitLoading ? <Typography variant="caption" color="primary">{t('astrology.loadingCurrentTransit')}</Typography> : null}
          {transitError ? <Typography variant="caption" color="error">{transitError}</Typography> : null}
        </Box>

        {/* Main Chart Display */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <NorthIndianChart chartData={chartData} transitChartData={transitChart} showTransitOverlay={showTransitOverlay} size={chartSize} rotationHouse={effectiveRotationHouse} />
        </Box>

        {/* Bhava Chalit Chart - Debug: Always show */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              
            </Typography>
            {chartData?.bhavaChalit ? (
              <BhavaChalitChart chartData={chartData} size={chartSize} rotationHouse={effectiveRotationHouse} />
            ) : (
              <Typography color="error">No BhavaChalit data found in chartData</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Planet Details */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(chartData.planets || {}).filter(([key, planet]) => shouldShowPlanet(key, planet)).map(([key, planet]) => {
            const rotatedHouse = rotateHouseNumber(planet.house);
            const rotatedBhavaHouse = rotateHouseNumber(planet.bhavaChalitHouse);

            return (
              <Chip
                key={key}
                label={`${getPlanetAbbr(t, planet.name || key)}: ${getSignAbbr(t, planet.sign.number)} (${getHouseShort(t, rotatedHouse || '')}${planet.bhavaChalitHouse !== undefined && rotatedBhavaHouse !== rotatedHouse ? `/${t('astrology.bhava')}${rotatedBhavaHouse}` : ''})`}
                variant="outlined"
                size="small"
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BirthChartDisplay;
